import { createBrowserRouter, Navigate } from 'react-router-dom';

import { ActivateScreen, LoginScreen, RegistrationScreen, RequireAuth } from '@/features/auth';
import {
  DashboardScreen,
  DeadSurtsScreen,
  InboxScreen,
  LaunchedScreen,
  MyCommitmentsScreen,
  TeamWorkloadScreen,
  WaitingIterationScreen,
  WaitingSurtsScreen,
  WokenSurtsScreen,
} from '@/features/commitments';
import { CampaignScreen, CampaignsScreen } from '@/features/campaigns';
import { ConstellationEntryScreen, ConstellationScreen, ElementScreen } from '@/features/elements';
import { SETTINGS_SECTION_ROUTES } from './settingsRoutes';

import { AppShell } from './AppShell';
import { SettingsShell } from './SettingsShell';

/**
 * Trasy. **Wszystkie ścieżki po angielsku, także w SPA** (decyzja 23) — adres
 * jest częścią produktu widoczną na zewnątrz (Jira, Slack, code review),
 * a produkt jest wielojęzyczny.
 *
 * `/register/:token` musi się zgadzać z tym, co serwer wystawia w
 * `RegistrationLinkResponse.Path` — pilnuje tego test po stronie API
 * (`AuthFlowTests.Sciezka_linku_rejestracyjnego_jest_ANGIELSKA...`).
 */
export const router = createBrowserRouter([
  { path: '/login', element: <LoginScreen /> },
  { path: '/register/:token', element: <RegistrationScreen /> },

  // ⭐ Aktywacja pierwszego admina (dec. 147). ⚠ Token NIE jest parametrem trasy — przychodzi
  //    we FRAGMENCIE adresu (`/activate#<token>`), którego przeglądarka nie wysyła na serwer.
  //    Gdyby był w ścieżce, siedziałby w logach żądań Kestrela i Caddy.
  { path: '/activate', element: <ActivateScreen /> },

  {
    path: '/',
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <DashboardScreen /> },

      // Slug tytułu jest OPCJONALNY i tylko dla czytelności — źródłem prawdy
      // jest `id` (spec 4A: tytuł się zmienia, linki nie mogą gnić).
      { path: 'element/:id', element: <ElementScreen /> },
      { path: 'element/:id/:slug', element: <ElementScreen /> },

      // ⚠ Trasa BEZ identyfikatora musi istnieć: prowadzi do niej pozycja „Constellation"
      // w menu. Zanim powstała, kliknięcie wpadało w `path: '*'` i cicho lądowało
      // na pulpicie — co wygląda jak awaria, a nie jak brak wyboru.
      { path: 'constellation', element: <ConstellationEntryScreen /> },
      { path: 'constellation/:id', element: <ConstellationScreen /> },
      { path: 'constellation/:id/:slug', element: <ConstellationScreen /> },

      // Sześć list z menu — OSOBNE ekrany, nie jeden z soczewkami (decyzja usera,
      // 2026-08-03: „elementy na tych listach są swoiste i będą otrzymywały różne
      // akcje, wyglądy, podglądy"). Wspólny jest tylko szkielet.
      //
      // ⚠ `surts/*` wypisane po jednej trasie zamiast `surts/:kind`: parametr kazałby
      // rozgałęziać się w środku komponentu, czyli robić z sześciu ekranów jeden
      // z przełącznikiem — dokładnie to, co decyzja odrzuciła.
      { path: 'commitments', element: <MyCommitmentsScreen /> },
      { path: 'surts/woken', element: <WokenSurtsScreen /> },
      { path: 'surts/waiting', element: <WaitingSurtsScreen /> },
      { path: 'iterations/waiting', element: <WaitingIterationScreen /> },
      { path: 'surts/dead', element: <DeadSurtsScreen /> },
      { path: 'inbox', element: <InboxScreen /> },
      { path: 'launched', element: <LaunchedScreen /> },
      { path: 'team-workload', element: <TeamWorkloadScreen /> },

      // Ekrany kampanii (E5). Wcześniej ta trasa przekierowywała na pulpit — ta sama
      // klasa usterki, co „Constellation nie prowadziło do drzewka".
      { path: 'campaigns', element: <CampaignsScreen /> },
      { path: 'campaigns/:id', element: <CampaignScreen /> },

      // ⚠ Importu NIE MA w trasach roboczych — mieszka w ustawieniach (`/settings/import`,
      // user 2026-08-08). ⛔ Nie zostawiamy tu też przekierowania: poprzednia wersja nigdy
      // nie została wdrożona, więc nikt tej ścieżki nie zna, a martwy alias to dług,
      // którego nikt nigdy nie skasuje.
    ],
  },

  /*
   * Ustawienia — RODZEŃSTWO powłoki, nie jej dziecko (2026-08-06).
   *
   * ⚠ To nie jest przesunięcie w drzewie tras dla porządku: dopóki `/settings` było
   * dzieckiem `AppShell`, ustawienia rysowały się w treści, obok menu z listami
   * roboczymi. Wyprowadzenie ich stąd JEST tą zmianą — wejście w ustawienia ma być
   * wyjściem z pracy, więc muszą mieć własną powłokę (`SettingsShell`: powrót,
   * własny spis sekcji, bez menu głównego).
   *
   * ⭐ Skutek uboczny, na który liczyliśmy: `AppShell` renderuje się teraz **tylko
   * w aplikacji roboczej**, więc zapamiętywanie „skąd przyszedłem" nie musi filtrować
   * ścieżek po prefiksie (patrz `lastAppRoute`).
   *
   * Każda sekcja ma własny adres, bo cała platforma jest sterowana URL-em (spec 4A):
   * „wejdź w ustawienia i kliknij trzecią pozycję z lewej" to nie jest instrukcja,
   * którą da się wysłać.
   */
  {
    path: '/settings',
    element: (
      <RequireAuth>
        <SettingsShell />
      </RequireAuth>
    ),
    // ⚠ Sekcje przychodzą z modułu `identity`, a nie są wypisane tutaj (2026-08-06). Wypisane
    // tu żyły w dwóch egzemplarzach — drugi w powłoce testu ustawień — więc każda nowa sekcja
    // wymagała dwóch wpisów, a rozjazd znaczył, że test bada inną aplikację niż wdrażana.
    children: [...SETTINGS_SECTION_ROUTES],
  },

  { path: '*', element: <Navigate to="/" replace /> },
]);
